import React from 'react';
import { getActiveSiteTheme } from '../services/api';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const PUBLIC_URL = process.env.PUBLIC_URL || '';

// Theme art comes from two places: files shipped in the website build under
// /img/..., and files admins upload, which live on the API under /themeassets/.
// The stored path says which, so nothing has to be flagged in the database.
export function resolveThemeAssetUrl(value) {
    if (!value) return value;
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith('/themeassets/')) return `${API_URL}${value}`;
    if (value.startsWith('/img/')) return `${PUBLIC_URL}${value}`;
    return value;
}

export const DEFAULT_THEME = {
    id: null,
    key: 'default',
    name: 'Default',
    isDefault: true,
    config: {
        logo: { url: '/img/franklyorigins.png', offsetX: 0, offsetY: 0 },
        askFrankLogo: { url: '/img/askfrankbeta.png', offsetX: 0, offsetY: 0 },
        bannerTile: { url: '/img/red_tile.webp', alignOffset: 32, offsetY: 18 },
        askFrankTile: { url: '/img/red_tile.webp' },
        bannerHeight: 155,
        furniHex: null,
        decorations: [],
    },
};

const num = (value, fallback) => (Number.isFinite(Number(value)) ? Number(value) : fallback);

const mergeSlot = (value, fallback) => ({
    url: value?.url || fallback.url,
    offsetX: num(value?.offsetX, fallback.offsetX ?? 0),
    offsetY: num(value?.offsetY, fallback.offsetY ?? 0),
});

// Fills in every slot a config might be missing, and returns fresh objects so a
// caller can edit the result without touching what it was built from.
//
// The API normalises Config on write, but a row saved before a field existed, a
// stale cache, or an API deployed behind the website will all arrive short. Run
// anything that comes off the wire through here before reading into it.
export function mergeThemeConfig(incoming) {
    const base = DEFAULT_THEME.config;
    const config = incoming || {};

    return {
        logo: mergeSlot(config.logo, base.logo),
        askFrankLogo: mergeSlot(config.askFrankLogo, base.askFrankLogo),
        bannerTile: {
            url: config.bannerTile?.url || base.bannerTile.url,
            alignOffset: num(config.bannerTile?.alignOffset, base.bannerTile.alignOffset),
            offsetY: num(config.bannerTile?.offsetY, base.bannerTile.offsetY),
        },
        askFrankTile: { url: config.askFrankTile?.url || base.askFrankTile.url },
        bannerHeight: num(config.bannerHeight, base.bannerHeight),
        furniHex: config.furniHex || null,
        decorations: (Array.isArray(config.decorations) ? config.decorations : []).map(d => ({ ...d })),
    };
}

function mergeTheme(incoming) {
    return {
        id: incoming?.id ?? null,
        key: incoming?.key || DEFAULT_THEME.key,
        name: incoming?.name || DEFAULT_THEME.name,
        isDefault: incoming?.isDefault === true,
        config: mergeThemeConfig(incoming?.config),
    };
}

let theme = DEFAULT_THEME;
let loadPromise = null;

const listeners = new Set();

const notify = () => {
    listeners.forEach(fn => {
        try { fn(); } catch {}
    });
};

export function getSiteTheme() {
    return theme;
}

export function loadSiteTheme(force = false) {
    if (loadPromise && !force) return loadPromise;

    loadPromise = getActiveSiteTheme()
        .then(res => {
            if (res?.success && res.theme) {
                theme = mergeTheme(res.theme);
                notify();
            }
            return theme;
        })
        .catch(() => theme);

    return loadPromise;
}

export function useSiteTheme() {
    const [, bump] = React.useReducer(x => x + 1, 0);

    React.useEffect(() => {
        listeners.add(bump);
        loadSiteTheme();
        return () => { listeners.delete(bump); };
    }, []);

    return theme;
}
